import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import {
  DatePicker, Select, Input, Button, Table, Space, Row, Col, Form,
  Card, Typography, Tag, Modal, Popconfirm, message, ConfigProvider,
} from 'antd';
import {
  SearchOutlined, DownloadOutlined, PlusOutlined, ReloadOutlined,
  DeleteOutlined, LinkOutlined, SyncOutlined, CalendarOutlined, EditOutlined, FormOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import ko_KR from 'antd/es/locale/ko_KR';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import '../styles/Common.css';
import { API_BASE } from '../config/api';
import { groupByEmployee } from '../utils/attendanceGrouping';
import { AuthContext } from '../contexts/AuthContext';
import { ManualEntryModal, DayEditModal } from './AttendanceManualModals';

dayjs.extend(customParseFormat);
dayjs.locale('ko');

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text, Paragraph } = Typography;

// Axios 인스턴스 (로그인 토큰 자동 부착) — 기존 PermissionsPage 패턴 복제
const api = axios.create({ baseURL: API_BASE });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 고용형태 한글 표기
const EMPLOYMENT_LABEL = { REGULAR: '정직원', OUTSOURCING: '아웃소싱' };

// 근무시간(분) → "X시간 Y분" 포맷
const formatWorkMinutes = (m) => {
  if (m == null) return '-';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h && mm) return `${h}시간 ${mm}분`;
  if (h) return `${h}시간`;
  return `${mm}분`;
};

const dash = (v) => (v == null || v === '' ? '-' : v);

// 다운로드 URL → blob 저장 (AttendanceReport.js 방식)
async function downloadFile(downloadUrl, filename) {
  try {
    const response = await fetch(downloadUrl);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('파일 다운로드 중 오류 발생:', error);
    message.error('엑셀 파일 다운로드 중 오류가 발생했습니다.');
  }
}

/* ──────────────────────────────────────────────
   출퇴근 요약 조회 섹션 ([v2] 사람별 그룹 + 펼침)
────────────────────────────────────────────── */
const SummarySection = ({ perms, reloadKey }) => {
  const [range, setRange] = useState([dayjs().startOf('month'), dayjs()]);
  const [employmentType, setEmploymentType] = useState('');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dayTarget, setDayTarget] = useState(null); // { employee_id, name, date }
  const canEdit = perms.create || perms.update || perms.delete;

  // [v2] 사람별 그룹핑(공용 유틸)
  const groups = useMemo(() => groupByEmployee(rows), [rows]);

  const buildFilters = () => {
    if (!range || !range[0] || !range[1]) return null;
    const filters = {
      from: range[0].format('YYYY-MM-DD'),
      to: range[1].format('YYYY-MM-DD'),
    };
    if (employmentType) filters.employment_type = employmentType;
    if (q && q.trim()) filters.q = q.trim();
    return filters;
  };

  const fetchSummary = useCallback(async () => {
    if (!range || !range[0] || !range[1]) {
      message.warning('조회 기간을 선택하세요.');
      return;
    }
    const params = {
      from: range[0].format('YYYY-MM-DD'),
      to: range[1].format('YYYY-MM-DD'),
    };
    if (employmentType) params.employment_type = employmentType;
    if (q && q.trim()) params.q = q.trim();

    setLoading(true);
    try {
      const { data } = await api.get('/api/attendance/summary', { params });
      setRows(data.data || []);
    } catch (e) {
      const msg = e.response?.data?.message || '출퇴근 요약 조회에 실패했습니다.';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [range, employmentType, q]);

  // 수기 입력 저장 후 같은 조건으로 다시 조회 (조회한 적이 있을 때만)
  useEffect(() => {
    if (reloadKey) fetchSummary();
  }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // [v3] 엑셀 응답이 { success, files: [...] } 배열 — 순회하며 각 파일 다운로드
  const handleExport = async () => {
    const filters = buildFilters();
    if (!filters) {
      message.warning('조회 기간을 선택하세요.');
      return;
    }
    setExporting(true);
    try {
      const { data } = await api.post('/api/attendance/summary/excel', filters);
      const files = data.files || [];
      if (files.length === 0) {
        message.info('다운로드할 데이터가 없습니다.');
        return;
      }
      for (const f of files) {
        // eslint-disable-next-line no-await-in-loop
        await downloadFile(
          f.downloadUrl,
          `출퇴근카드_${f.label}_${filters.from}~${filters.to}.xlsx`,
        );
      }
    } catch (e) {
      const msg = e.response?.data?.message || '엑셀 생성에 실패했습니다.';
      message.error(msg);
    } finally {
      setExporting(false);
    }
  };

  // 부모 행(사람 단위 요약)
  const parentColumns = [
    { key: 'name', title: '성명', dataIndex: 'name', width: 100, render: (v) => <Text strong>{dash(v)}</Text> },
    {
      key: 'employment_type', title: '고용형태', dataIndex: 'employment_type', width: 90,
      render: (v) => <Tag color={v === 'OUTSOURCING' ? 'orange' : 'blue'}>{EMPLOYMENT_LABEL[v] || dash(v)}</Tag>,
    },
    { key: 'department', title: '부서', dataIndex: 'department', width: 120, render: dash },
    { key: 'position', title: '직급', dataIndex: 'position', width: 100, render: dash },
    { key: 'work_days', title: '근무일수', dataIndex: 'work_days', width: 90, render: (v) => `${v}일` },
    {
      key: 'total_work_minutes', title: '총 근무시간', dataIndex: 'total_work_minutes', width: 120,
      render: (v, record) => (record.has_work_minutes ? formatWorkMinutes(v) : '-'),
    },
  ];

  // 펼침(자식) 행: 일자별 상세
  const detailColumns = [
    { key: 'work_date', title: '근무일자', dataIndex: 'work_date', width: 120, render: dash },
    { key: 'check_in', title: '출근', dataIndex: 'check_in', width: 180, render: dash },
    { key: 'check_out', title: '퇴근', dataIndex: 'check_out', width: 180, render: dash },
    { key: 'work_minutes', title: '근무시간', dataIndex: 'work_minutes', width: 120, render: formatWorkMinutes },
    {
      key: 'manual', title: '', width: canEdit ? 130 : 70,
      render: (_, r) => (
        <Space size={4}>
          {r.manual_count > 0 && <Tag color="orange">수기</Tag>}
          {canEdit && (
            <Button size="small" icon={<EditOutlined />} onClick={() => setDayTarget({ employee_id: r.employee_id, name: r.name, date: r.work_date })}>
              수정
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const expandedRowRender = (record) => (
    <Table
      dataSource={record.details}
      columns={detailColumns}
      rowKey={(r) => `${r.employee_id}-${r.work_date}`}
      size="small"
      pagination={false}
    />
  );

  return (
    <Card style={{ marginBottom: 24 }}>
      <Form layout="vertical">
        <Row gutter={16}>
          <Col xs={24} md={10}>
            <Form.Item label="조회 기간">
              <RangePicker
                value={range}
                onChange={(v) => setRange(v)}
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={5}>
            <Form.Item label="고용형태">
              <Select value={employmentType} onChange={setEmploymentType} style={{ width: '100%' }}>
                <Option value="">전체</Option>
                <Option value="REGULAR">정직원</Option>
                <Option value="OUTSOURCING">아웃소싱</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={9}>
            <Form.Item label="직원명">
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onPressEnter={fetchSummary}
                placeholder="직원 이름"
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>
        <Space>
          <Button type="primary" icon={<SearchOutlined />} onClick={fetchSummary} loading={loading}>
            조회
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport} loading={exporting}>
            엑셀 다운로드
          </Button>
        </Space>
      </Form>

      <Table
        style={{ marginTop: 16 }}
        dataSource={groups}
        columns={parentColumns}
        rowKey="employee_id"
        loading={loading}
        size="small"
        scroll={{ x: 900 }}
        expandable={{ expandedRowRender }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
      <DayEditModal
        api={api} open={!!dayTarget} target={dayTarget} perms={perms}
        onClose={() => setDayTarget(null)} onSaved={fetchSummary}
      />
    </Card>
  );
};

/* ──────────────────────────────────────────────
   외부 조회 링크(토큰) 관리 — 모달
────────────────────────────────────────────── */
const AccessTokenModal = ({ open, onClose }) => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const buildPublicLink = (publicPath) => `${window.location.origin}${publicPath || ''}`;

  const fetchTokens = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/attendance/access-tokens');
      setTokens(data.data || []);
    } catch (e) {
      message.error('접근 링크 목록 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) fetchTokens(); }, [open, fetchTokens]);

  const openModal = () => {
    form.resetFields();
    setModalOpen(true);
  };

  const handleIssue = async (values) => {
    setSaving(true);
    try {
      const payload = {};
      if (values.label && values.label.trim()) payload.label = values.label.trim();
      if (values.vendor_name && values.vendor_name.trim()) payload.vendor_name = values.vendor_name.trim();
      if (values.expires_at) payload.expires_at = values.expires_at.format('YYYY-MM-DD');
      // [v2] 공개 슬러그(선택) — 지정 시 /attendance/{slug}로 토큰 없이 접근 가능
      if (values.public_slug && values.public_slug.trim()) payload.public_slug = values.public_slug.trim();

      const { data } = await api.post('/api/attendance/access-tokens', payload);
      const link = buildPublicLink(data.data?.public_path);
      message.success('접근 링크가 발급되었습니다.');
      Modal.success({
        title: '발급된 외부 조회 링크',
        content: (
          <Paragraph copyable={{ text: link }} style={{ wordBreak: 'break-all', marginBottom: 0 }}>
            {link}
          </Paragraph>
        ),
      });
      setModalOpen(false);
      fetchTokens();
    } catch (e) {
      // [v2] 409(슬러그 중복)·400(형식/예약어) 서버 메시지 그대로 노출
      const msg = e.response?.data?.message || '링크 발급에 실패했습니다.';
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (id) => {
    try {
      await api.delete(`/api/attendance/access-tokens/${id}`);
      message.success('링크가 폐기되었습니다.');
      fetchTokens();
    } catch (e) {
      const msg = e.response?.data?.message || '링크 폐기에 실패했습니다.';
      message.error(msg);
    }
  };

  const columns = [
    { key: 'label', title: '메모', dataIndex: 'label', width: 140, render: dash },
    { key: 'vendor_name', title: '업체명', dataIndex: 'vendor_name', width: 120, render: (v) => (v ? v : '전체') },
    {
      key: 'public_slug', title: '공개 슬러그', dataIndex: 'public_slug', width: 120,
      render: (v) => (v ? <Tag color="geekblue">{v}</Tag> : '-'),
    },
    {
      key: 'status', title: '상태', dataIndex: 'status', width: 90,
      render: (v) => <Tag color={v === 'ACTIVE' ? 'green' : 'red'}>{v === 'ACTIVE' ? '활성' : '폐기'}</Tag>,
    },
    { key: 'expires_at', title: '만료일', dataIndex: 'expires_at', width: 160, render: (v) => (v ? v : '무기한') },
    { key: 'last_accessed_at', title: '최근 접속', dataIndex: 'last_accessed_at', width: 160, render: dash },
    {
      key: 'link', title: '링크', width: 120,
      render: (_, record) => (
        <Typography.Text
          copyable={{ text: buildPublicLink(record.public_path), icon: <LinkOutlined /> }}
        >
          복사
        </Typography.Text>
      ),
    },
    {
      key: 'action', title: '작업', width: 90,
      render: (_, record) => (
        record.status === 'ACTIVE' ? (
          <Popconfirm
            title="이 링크를 폐기하시겠습니까?"
            onConfirm={() => handleRevoke(record.id)}
            okText="예" cancelText="아니오"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>폐기</Button>
          </Popconfirm>
        ) : null
      ),
    },
  ];

  return (
    <Modal
      title="외부 조회 링크 관리"
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}
      destroyOnClose
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        아웃소싱 관리자(비회원)에게 전달할 출퇴근 조회 링크를 발급/폐기합니다. 링크는 아웃소싱 직원 데이터만 조회할 수 있습니다.
      </Text>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openModal}>링크 발급</Button>
        <Button icon={<ReloadOutlined />} onClick={fetchTokens}>새로고침</Button>
      </Space>
      <Table
        dataSource={tokens}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        scroll={{ x: 1000 }}
        pagination={{ pageSize: 10 }}
      />
      <Modal
        title="외부 조회 링크 발급"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText="발급"
        cancelText="취소"
        confirmLoading={saving}
      >
        <Form form={form} layout="vertical" onFinish={handleIssue}>
          <Form.Item name="label" label="메모">
            <Input placeholder="예: OO용역 김반장" />
          </Form.Item>
          <Form.Item name="vendor_name" label="업체명 (선택)" extra="미지정 시 전체 아웃소싱 직원 조회 가능">
            <Input placeholder="특정 업체만 허용하려면 업체명 입력" />
          </Form.Item>
          <Form.Item name="expires_at" label="만료일 (선택)" extra="미지정 시 무기한">
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item
            name="public_slug"
            label="공개 슬러그 (선택)"
            extra="소문자·숫자·하이픈만. 지정 시 /attendance/{slug} 로 토큰 없이 접근 가능"
            rules={[{ pattern: /^[a-z0-9-]{2,50}$/, message: '소문자·숫자·하이픈 2~50자만 가능합니다.' }]}
          >
            <Input placeholder="예: knc (비우면 토큰 링크만)" />
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
};

/* ──────────────────────────────────────────────
   [v3] 공휴일 관리 섹션 (최소 UI)
────────────────────────────────────────────── */
const HOLIDAY_SOURCE_LABEL = { API: '공공데이터', MANUAL: '수동' };

const HolidayModal = ({ open, onClose }) => {
  const currentYear = dayjs().year();
  const [year, setYear] = useState(String(currentYear));
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form] = Form.useForm();

  const yearOptions = useMemo(() => {
    const arr = [];
    for (let y = currentYear + 1; y >= currentYear - 3; y -= 1) arr.push(String(y));
    return arr;
  }, [currentYear]);

  const fetchHolidays = useCallback(async (targetYear) => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/attendance/holidays', { params: { year: targetYear } });
      setHolidays(data.data || []);
    } catch (e) {
      message.error(e.response?.data?.message || '공휴일 목록 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) fetchHolidays(year); }, [open, year, fetchHolidays]);

  const handleAdd = async (values) => {
    setAdding(true);
    try {
      await api.post('/api/attendance/holidays', {
        holiday_date: values.holiday_date.format('YYYY-MM-DD'),
        name: values.name.trim(),
      });
      message.success('공휴일이 등록되었습니다.');
      form.resetFields();
      fetchHolidays(year);
    } catch (e) {
      message.error(e.response?.data?.message || '공휴일 등록에 실패했습니다.');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/attendance/holidays/${id}`);
      message.success('공휴일이 삭제되었습니다.');
      fetchHolidays(year);
    } catch (e) {
      message.error(e.response?.data?.message || '공휴일 삭제에 실패했습니다.');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/api/attendance/holidays/sync', { year });
      message.success(data.message || `공휴일 동기화 완료 (${data.upserted_count ?? 0}건)`);
      fetchHolidays(year);
    } catch (e) {
      // 키 미설정(400)·외부 API 오류(502) 서버 메시지 그대로 노출
      message.error(e.response?.data?.message || '공휴일 동기화에 실패했습니다.');
    } finally {
      setSyncing(false);
    }
  };

  const columns = [
    { key: 'holiday_date', title: '날짜', dataIndex: 'holiday_date', width: 130, render: dash },
    { key: 'name', title: '명칭', dataIndex: 'name', render: dash },
    {
      key: 'source', title: '출처', dataIndex: 'source', width: 110,
      render: (v) => <Tag color={v === 'API' ? 'blue' : 'default'}>{HOLIDAY_SOURCE_LABEL[v] || dash(v)}</Tag>,
    },
    {
      key: 'action', title: '작업', width: 80,
      render: (_, record) => (
        <Popconfirm
          title="이 공휴일을 삭제하시겠습니까?"
          onConfirm={() => handleDelete(record.id)}
          okText="예" cancelText="아니오"
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Modal
      title="공휴일 관리"
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      destroyOnClose
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        엑셀 "출퇴근 카드"의 공휴일 배경 표시에 사용됩니다. 공공데이터포털 특일정보로 일괄 동기화하거나 수동으로 등록/삭제할 수 있습니다.
      </Text>
      <Space style={{ marginBottom: 12 }} wrap>
        <Select value={year} onChange={setYear} style={{ width: 110 }}>
          {yearOptions.map((y) => <Option key={y} value={y}>{y}년</Option>)}
        </Select>
        <Button icon={<SyncOutlined />} onClick={handleSync} loading={syncing}>공공데이터 동기화</Button>
        <Button icon={<ReloadOutlined />} onClick={() => fetchHolidays(year)}>새로고침</Button>
      </Space>
      <Form form={form} layout="inline" onFinish={handleAdd} style={{ marginBottom: 16 }}>
        <Form.Item name="holiday_date" rules={[{ required: true, message: '날짜를 선택하세요.' }]}>
          <DatePicker placeholder="날짜" format="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item name="name" rules={[{ required: true, message: '명칭을 입력하세요.' }]}>
          <Input placeholder="명칭 (예: 임시공휴일)" style={{ width: 200 }} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" icon={<PlusOutlined />} htmlType="submit" loading={adding}>추가</Button>
        </Form.Item>
      </Form>
      <Table
        dataSource={holidays}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 15 }}
      />
    </Modal>
  );
};

const AttendanceInfo = () => {
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [holidayModalOpen, setHolidayModalOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const { auth } = useContext(AuthContext);
  const yes = (v) => v === 1 || v === '1' || v === true;
  const commute = (auth?.menuPermissions || []).find((p) => p.menu_key === 'commute') || {};
  const perms = { create: yes(commute.can_create), update: yes(commute.can_update), delete: yes(commute.can_delete) };

  return (
    <ConfigProvider locale={ko_KR}>
      <div className="common-container">
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[8, 8]}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>출퇴근 정보</Title>
          </Col>
          <Col>
            <Space wrap>
              {perms.create && (
                <Button type="primary" icon={<FormOutlined />} onClick={() => setManualOpen(true)}>
                  출퇴근 수기 입력
                </Button>
              )}
              <Button icon={<LinkOutlined />} onClick={() => setLinkModalOpen(true)}>
                외부 조회 링크 관리
              </Button>
              <Button icon={<CalendarOutlined />} onClick={() => setHolidayModalOpen(true)}>
                공휴일 관리
              </Button>
            </Space>
          </Col>
        </Row>

        <SummarySection perms={perms} reloadKey={reloadKey} />
        <ManualEntryModal api={api} open={manualOpen} onClose={() => setManualOpen(false)} onSaved={() => setReloadKey((k) => k + 1)} />

        <AccessTokenModal open={linkModalOpen} onClose={() => setLinkModalOpen(false)} />
        <HolidayModal open={holidayModalOpen} onClose={() => setHolidayModalOpen(false)} />
      </div>
    </ConfigProvider>
  );
};

export default AttendanceInfo;
